import React, { useState, useEffect } from 'react';
import { MessageCircle, Facebook, Instagram, Linkedin, ChevronRight, ChevronDown, Home, Car, Wallet, Building2, Check, Star, Mail, Phone, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { services, teamMembers, lenders, faqs, googleReviews } from '../mockData';
import Header from '../components/Header';
import Footer from '../components/Footer';
import MortgageCalculator from '../components/MortgageCalculator';

const iconMap = {
  Home: Home,
  Car: Car,
  Wallet: Wallet,
  Building2: Building2
};

const LandingPage = () => {
  const [activeService, setActiveService] = useState(null);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-white z-0"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h1 className="text-5xl lg:text-6xl font-bold leading-tight">
                <span className="block mt-2" style={{ color: 'rgb(8, 120, 191)', style: 'Exo'}}>
                    Your Trusted Finance Consultants
                  </span>
              </h1>
              <p className="text-xl text-gray-600">
                We're proud to be one of Australia's leading mortgage brokers, trusted by first-home buyers and investors nationwide.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  style={{ backgroundColor: 'rgb(8, 120, 191)' }}
                  className="text-white hover:opacity-90 transition-opacity text-lg px-8 py-6"
                  onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                >
                  Contact Us Today
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="text-lg px-8 py-6 border-2"
                  style={{ borderColor: 'rgb(8, 120, 191)', color: 'rgb(8, 120, 191)' }}
                  onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
                >
                  Explore Services
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0878BF]/10 to-transparent rounded-3xl transform rotate-3"></div>
              <img
                src="/Family_Keys.jpg"
                alt="A family holding the keys to their new home"
                className="relative rounded-3xl shadow-2xl w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-6" style={{ color: 'rgb(8, 120, 191)' }}>
              About Us
            </h2>
            <h1 className="text-lg text-gray-600 text-center mb-12">
              Finding the right loan shouldn't feel like a fight. With us, it isn't.
            </h1>

            <div className="grid grid-cols-1 md:grid-cols-1 gap-8 items-center mb-12">
              <div className="space-y-4">
                <h1 className="text-xl mb-6 text-gray-700 text-font-size-24">
                  With over 20 years of combined experience, our team knows how to get you across the line — even when your situation feels complicated. Self-employed, complex income, tricky credit history? We've likely seen it before, and we don't give up easily.
                </h1>
                <p className="text-xl text-gray-700">
                  We believe you deserve to understand your own finance journey, not just sign where you're told. So we keep things clear, honest, and genuinely informed every step of the way.
                </p>
                <p className="text-xl text-gray-700">
                  We understand that lending looks different depending on where you live. That's why no matter where you are, our nearby experts can give you the run-down of the local markets - you can talk to us via video conference as well, no matter the location!
                </p>
                <p className="text-xl text-gray-700">
                  Real experience. Real people. A loan that actually fits you.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section id="why-us" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-6" style={{ color: 'rgb(8, 120, 191)' }}>
              Why Choose Us?
            </h2>
            <p className="text-lg text-gray-600 text-center mb-12">
              We strive to provide exceptional service and the best outcomes for our clients
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-2 hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2" style={{ color: 'rgb(8, 120, 191)' }}>
                    <Check className="h-6 w-6" />
                    <span>Competitive Rates</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    We strive to secure the most competitive mortgage rates, ensuring you get the best deal for your financial needs. With over 20 years of combined experience, we know how to get you across the line — even when your situation feels complicated. We keep things clear and genuinely informed every step of the way, with local experts near you who understand your market. Even if you don't live in a major city, wherever you are, we're only a video conference away!
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2" style={{ color: 'rgb(8, 120, 191)' }}>
                    <Check className="h-6 w-6" />
                    <span>Expert Advice</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    With over 20 years of experience, our team of top-rated mortgage brokers provides personalised guidance tailored to your unique financial situation. We make it our job to ensure you know what's going on, so your home loan truly belongs to you.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2" style={{ color: 'rgb(8, 120, 191)' }}>
                    <Check className="h-6 w-6" />
                    <span>Streamlined Process</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Experience a stress-free mortgage application process with our expert guidance at every step.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2" style={{ color: 'rgb(8, 120, 191)' }}>
                    <Check className="h-6 w-6" />
                    <span>Local Knowledge</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    As specialists in the Australian mortgage market, we understand local nuances and regulations to provide informed advice.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-6" style={{ color: 'rgb(8, 120, 191)' }}>
              Our Services
            </h2>
            <p className="text-lg text-gray-600 text-center mb-12">
              Tailored finance solutions for every need
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {services.map((service) => {
                const IconComponent = iconMap[service.icon];
                const isExpanded = activeService === service.id;

                return (
                  <Card
                    key={service.id}
                    className="border-2 hover:shadow-xl transition-all cursor-pointer"
                    onClick={() => setActiveService(isExpanded ? null : service.id)}
                    style={{ borderColor: isExpanded ? 'rgb(8, 120, 191)' : '#e5e7eb' }}
                  >
                    <CardHeader>
                      <CardTitle className="flex items-center space-x-3">
                        <div
                          className="p-3 rounded-lg"
                          style={{ backgroundColor: 'rgb(8, 120, 191)', color: 'white' }}
                        >
                          {IconComponent && <IconComponent className="h-6 w-6" />}
                        </div>
                        <span class="flex flex-col sm:flex-row gap-4 grid grid-cols-1 md:grid-cols-2 gap-6">
                          {service.title}<ChevronDown className="h-5 w-5" />
                        </span>
                      </CardTitle>
                      <CardDescription className="text-base mt-2">
                        {service.shortDesc}
                      </CardDescription>
                    </CardHeader>
                    {isExpanded && (
                      <CardContent>
                        <p className="text-gray-600 leading-relaxed">
                          {service.fullDesc}
                        </p>
                      </CardContent>
                    )}
                  </Card>
                );
              })}
            </div>
            <p className="text-lg text-gray-600 text-center mb-12">
              <div className="h-6 w-px"></div>
              ...And more! Contact us at info@findmealender.com.au to ask about more tailored financial solutions.
            </p>
          </div>
        </div>
      </section>

      {/* Partnered Lenders Carousel */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-6" style={{ color: 'rgb(8, 120, 191)' }}>
              Our Partnered Lenders
            </h2>
            <p className="text-lg text-gray-600 text-center mb-12">
              We work with Australia's leading banks and lenders
            </p>

            <div className="relative overflow-hidden">
              <div className="flex animate-scroll-infinite">
                {/* Triple the lenders array for seamless infinite loop */}
                {[...lenders, ...lenders, ...lenders].map((lender, index) => (
                  <div
                    key={`${lender.id}-${index}`}
                    className="flex-shrink-0 w-56 mx-4"
                  >
                    <div className="bg-white rounded-lg shadow-md p-6 flex items-center justify-center h-24 hover:shadow-lg transition-shadow">
                      <img
                        src={lender.logo}
                        alt={lender.name}
                        className="w-full h-auto max-h-16 object-contain"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="flex justify-center items-center space-x-2 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-8 w-8 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <h2 className="text-4xl font-bold mb-4" style={{ color: 'rgb(8, 120, 191)' }}>
                5 Star Google Reviews
              </h2>
              <p className="text-lg text-gray-600">
                See what our satisfied clients have to say
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {googleReviews.map((review) => (
                <a href={review.link} target="_blank" rel="noopener noreferrer">
                  <Card key={review.id} className="border-2 hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-3">
                          <Avatar>
                              <img 
                                src={review.avatar} 
                                alt={review.name}
                                className="w-full h-full object-cover"
                              />
                          </Avatar>
                          <div>
                            <p className="font-semibold text-gray-900">{review.name}</p>
                            <p className="text-sm text-gray-500">{review.date}</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex space-x-1 mt-2 ">
                        {[...Array(review.rating)].map((_, i) => (
                          <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                        ))}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-700 leading-relaxed">{review.text}</p>
                      <div className="h-6 w-px"></div>
                      <ChevronRight className="h-4 w-4" />
                    </CardContent>
                  </Card>
                </a>
              ))}
            </div>

            <div className="text-center mt-8">
              <a
                href="https://www.google.com/maps/place/Find+Me+A+Lender/@-37.7604864,144.6933629,16z/data=!4m6!3m5!1s0x6ad6899e6cbb3dfb:0xe3b124caa5929c9c!8m2!3d-37.7603152!4d144.6952763!16s%2Fg%2F11mk47_ny3"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 text-gray-600 hover:text-[#0878BF] transition-colors font-medium"
              >
                <span>See all our Google Reviews</span>
                <ChevronRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-6" style={{ color: 'rgb(8, 120, 191)' }}>
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600 text-center mb-12">
              Find answers to common questions about our services
            </p>

            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-white rounded-lg px-6 border-2 border-gray-200"
                >
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Mortgage Calculator Section */}
      <section id="calculator" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-6" style={{ color: 'rgb(8, 120, 191)' }}>
              Mortgage Calculator
            </h2>
            <p className="text-lg text-gray-600 text-center mb-12">
              Calculate your potential mortgage payments
            </p>

            <MortgageCalculator />

            <div className="mt-8 text-center">
              <Button
                size="lg"
                variant="outline"
                style={{ borderColor: 'rgb(8, 120, 191)', color: 'rgb(8, 120, 191)' }}
                onClick={() => window.location.href = '/learn-more'}
              >
                Learn More About Finance
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="team" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-6" style={{ color: 'rgb(8, 120, 191)' }}>
              Meet Our Team
            </h2>
            <p className="text-lg text-gray-600 text-center mb-12">
              Experienced professionals dedicated to your financial success
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {teamMembers.map((member) => (
                <Card key={member.id} className="border-2 hover:shadow-xl transition-shadow overflow-hidden">
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <CardHeader>
                    <CardTitle>{member.name}</CardTitle>
                    <CardDescription className="text-base" style={{ color: 'rgb(8, 120, 191)' }}>
                      {member.role}
                    </CardDescription>
                    <p className="text-sm text-gray-500 mt-2">{member.credentials}</p>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-6" style={{ color: 'rgb(8, 120, 191)' }}>
              Contact Us
            </h2>
            <p className="text-lg text-gray-600 text-center mb-12">
              Ready to take the first step toward your dream home? Talk to an expert today!
            </p>

            <div className="flex justify-center">
              {/* Contact Info */}
              <div className="space-y-8 w-full">
                <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8">
                  <h3 className="text-2xl font-bold mb-6" style={{ color: 'rgb(8, 120, 191)' }}>
                    Get In Touch
                  </h3>

                  <div className="space-y-6">
                    <div className="flex items-start space-x-8">
                      <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgb(8, 120, 191)' }}>
                        <a href="https://www.google.com/maps/dir//The+Hub,+Old+Coach+Rd,+Upper+Coomera+QLD+4209/@-27.8560768,153.305088,14z/data=!4m8!4m7!1m0!1m5!1m1!1s0x6b9113f668f91425:0x98cb96be0c32091c!2m2!1d153.2995599!2d-27.8528332?entry=ttu&g_ep=EgoyMDI2MDIxMS4wIKXMDSoASAFQAw%3D%3D" target="_blank" rel="noopener noreferrer">
                          <MapPin className="h-6 w-6 text-white hover:text-[#6793D1] transition-colors" />
                        </a>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 ">Head Office</p>
                        <a href="https://www.google.com/maps/dir//The+Hub,+Old+Coach+Rd,+Upper+Coomera+QLD+4209/@-27.8560768,153.305088,14z/data=!4m8!4m7!1m0!1m5!1m1!1s0x6b9113f668f91425:0x98cb96be0c32091c!2m2!1d153.2995599!2d-27.8528332?entry=ttu&g_ep=EgoyMDI2MDIxMS4wIKXMDSoASAFQAw%3D%3D" target="_blank" rel="noopener noreferrer">
                          <p className="text-gray-600 hover:text-[#6793D1] transition-colors">THE HUB, Shop 14, 90 Days Road</p>
                          <p className="text-gray-600 hover:text-[#6793D1] transition-colors">Upper Coomera QLD 4209</p>
                        </a>
                      </div>
                      {/* <div>
                        <p className="font-semibold text-gray-900 ">Cairns Office</p>
                        <a href="" target="_blank" rel="noopener noreferrer">
                          <p className="text-gray-600 hover:text-[#6793D1] transition-colors">266 Mulgrave Road</p>
                          <p className="text-gray-600 hover:text-[#6793D1] transition-colors">Westcourt QLD 4870</p>
                        </a>
                      </div> */}
                    </div>

                    <div className="flex items-start space-x-8">
                      <a href="tel:1300078494" className="text-gray-600 hover:text-[#0878BF]">
                        <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgb(8, 120, 191)' }}>
                          <Phone className="h-6 w-6 text-white hover:text-[#6793D1] transition-colors" />
                        </div>
                      </a>
                      <div>
                        <div className="grid grid-cols-1 md:grid-cols-2">
                          <p className="font-semibold text-gray-900">Phone</p>
                          <a href="tel:1300078494" className="text-gray-600 hover:text-[#0878BF]">
                            1300 078 494
                          </a>
                          <p className="font-semibold text-gray-900">Mobile</p>
                          <a href="tel:0466553335" className="text-gray-600 hover:text-[#0878BF]">
                            0466 553 335
                          </a>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start space-x-8">
                      <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgb(8, 120, 191)' }}>
                        <a href="mailto:info@findmealender.com.au"><Mail className="h-6 w-6 text-white hover:text-[#6793D1] transition-colors" /></a>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">Email</p>
                        <a href="mailto:info@findmealender.com.au" className="text-gray-600 hover:text-[#0878BF]">
                          info@findmealender.com.au
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 pt-8 border-t border-gray-200 flex gap-6">
                    <a href="https://www.facebook.com/624072684675262"><Facebook className="h-12 w-12 text-[#0878BF] hover:text-[#6793D1] transition-colors" /></a>
                    <a href="https://www.instagram.com/find_me_a_lender/"><Instagram className="h-12 w-12 text-[#0878BF] hover:text-[#6793D1] transition-colors" /></a>
                    <a href="https://www.linkedin.com/company/find-me-a-lender/"><Linkedin className="h-12 w-12 text-[#0878BF] hover:text-[#6793D1] transition-colors" /></a>
                    <a href="https://wa.me/61466553335"><MessageCircle className="h-12 w-12 text-[#0878BF] hover:text-[#6793D1] transition-colors" /></a>
                </div>

                <div className="mt-8 pt-8 border-t border-gray-200">
                  <p className="font-semibold text-gray-900 mb-2">Office Hours</p>
                  <p className="text-gray-600">Monday - Friday: 9:00 AM - 5:00 PM</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Consultation appointments available outside office hours upon request
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
    </div>
      </section >
  <Footer />
    </div >
  );
};

export default LandingPage;
